import { Metamodel } from 'app/model/base/model';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { IViolation } from '../../../model/base/annotation';
import { Dataset } from '../../../model/dataset/dataset';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { FilePathMode } from 'app/registries/consts/metadata-consts';
import { 
    compressionCodecType,
    DelimitedTextDataset,
    Encoding,
    ParquetDataset,
    AzureBlobStorageLinkedService,
    azureStorageAuthTypes 
} from 'shell-models-lib';
import { charValueConst, rowDelimiters } from 'app/shared/components/fileformat/fileformat.constants';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { getDirectCopyErrorMessage, getConnectionAsSinkDirectCopyErrorMessage } from '../activity-validation-message.util';
import { format, formatListWithConjunction, stringIsNullOrEmpty } from '../string.util';
import { wildcardFileNameIsNull, wildcardFolderPathIsNull } from '../activity-validation.util';
import { FormatTypeCopySource } from 'app/model/pipeline/copysource/format-type.copysource';
import { BlobTypeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/blob-type-store-read-settings';
import { DelimitedTextCopySource } from 'app/model/pipeline/copysource/delimitedtext.source';
import { DelimitedTextFormatReadSettings } from 'app/model/pipeline/copysource/format-read-settings/delimited-text-format-read-settings';

/**
 *  For Teradata sink direct copy, 
 *  it only supports AccountKey and SAS URI authentication for Blob and Gen2
 */
export function validateSourceAuthTypesForTeradata(
    activity: CopyActivity,
    sourceDataset: Dataset,
    sourceLinkedservice: LinkedService,
    ifEnabledStaging: boolean,
): IViolation[] {
    const violations: IViolation[] = [];
    const lsForValidation = ifEnabledStaging ? activity.stagingSettings.storageLinkedService : sourceLinkedservice;
    const messagePrefix = ifEnabledStaging
        ? format(stringResources.Connection_StagedCopy, stringResources.Teradata_DisplayText)
        : format(stringResources.Connection_DirectCopy_Template, stringResources.To.toLocaleLowerCase(), stringResources.Teradata_DisplayText);
    const supportSourceDisplayTexts: string[] = [
            stringResources.AzureBlobStorage_DisplayText,
            stringResources.AzureBlobFS_DisplayText
        ];
    const allowedDirectCopyAuthTypes: string[] = [
        azureStorageAuthTypes.AccountKeyAuthentication,
        azureStorageAuthTypes.SasUriAuthentication
    ];
    // validate direct copy to Blob or Gen2 only supports AccountKey and SAS URI authentication
    if (
        (lsForValidation instanceof AzureBlobStorageLinkedService || lsForValidation instanceof AzureBlobFSLinkedService) &&
        !allowedDirectCopyAuthTypes.includes(lsForValidation.authenticationMethod) &&
        !lsForValidation.fullyByJsonEditor
    ) {
        violations.push({
            message: () =>
                messagePrefix +
                format(
                    stringResources.DirectCopyOnlySuppported_ErrorTemplate,
                    format(
                        stringResources.CopyCommand_CopyMethod_CopyCommandSourceAuthType_Error,
                        formatListWithConjunction(supportSourceDisplayTexts),
                        formatListWithConjunction(allowedDirectCopyAuthTypes)
                    )
                ),
            redirectTo: {
                entity: ifEnabledStaging ? activity : sourceDataset,
                attribute: ifEnabledStaging
                    ? CopyActivity.ENABLE_STAGING
                    : Metamodel.modelOf(sourceDataset).attribute('linkedService')
            }
        });
    }
    return violations;
}

/**
 *  Validate source settings for direct copy with Teradata sink
 *  It does not support additional columns
 *  When is auzre blob & gen2 as source, filePathMode only support DATASET and PREFIX
 */
export function validateSourceSettingsForTeradata(
    activity: CopyActivity
): IViolation[] {
    // const dataset = activity.sourceDataset;
    const violations: IViolation[] = [];
    // validate additional columns
    if (!arrayIsNullOrEmpty(activity.source?.additionalColumns)) {
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    stringResources.DirectCopy_AdditionalColumns_Error,
                    stringResources.CopySource_AdditionalColumns,
                    stringResources.Teradata_DisplayText,
                    'notSupport'
                ),
            redirectTo: {
                attribute: CopyActivity.ENABLE_STAGING
            }
        });
    }

    if (
        activity.source &&
        (activity.source as FormatTypeCopySource).storeSettings instanceof BlobTypeStoreReadSettings
    ) {
        const storeSettings = (activity.source as FormatTypeCopySource).storeSettings as BlobTypeStoreReadSettings;
        // not support blob & gen2 Wildcard
        if (!wildcardFileNameIsNull(storeSettings) || !wildcardFolderPathIsNull(storeSettings)) {
            violations.push({
                message: () =>
                    getConnectionAsSinkDirectCopyErrorMessage(
                        activity,
                        stringResources.DirectCoppy_Wildcard_Error,
                        stringResources.WildcardPath_Label,
                        stringResources.Teradata_DisplayText,
                        'notSupport'
                    ),
                redirectTo: {
                    attribute: CopyActivity.ENABLE_STAGING
                }
            });
        }

        // not support blob & gen2 List of files
        if (
            storeSettings.filePathMode === FilePathMode.LIST &&
            !stringIsNullOrEmpty(Metamodel.modelOf(storeSettings).attribute('fileListPath').value(storeSettings))
        ) {
            violations.push({
                message: () =>
                    getConnectionAsSinkDirectCopyErrorMessage(
                        activity,
                        stringResources.DirectCoppy_List_Error,
                        stringResources.DataFlowSourceSettingsListOfFiles, // 'List of files'
                        stringResources.Teradata_DisplayText,
                        'notSupport'
                    ),
                redirectTo: {
                    attribute: Metamodel.modelOf(storeSettings).attribute('fileListPath')
                }
            });
        }
        // validate partition discovery
        if (storeSettings.enablePartitionDiscovery === true) {
            violations.push({
                message: () =>
                    getConnectionAsSinkDirectCopyErrorMessage(
                        activity,
                        stringResources.PartitionDiscovery_In_Compatible_With_Copy_Command,
                        stringResources.EnablePartitionDiscovery,
                        stringResources.Teradata_DisplayText,
                        'notSupport'
                    ),
                redirectTo: {
                    attribute: Metamodel.modelOf(storeSettings).attribute('enablePartitionDiscovery')
                }
            });
        }
        // validate Max concurrent connections
        if (
            !stringIsNullOrEmpty(Metamodel.modelOf(storeSettings).attribute('maxConcurrentConnections').value(storeSettings))
        ) {
            violations.push({
                message: () =>
                    getConnectionAsSinkDirectCopyErrorMessage(
                        activity,
                        stringResources.DirectCoppy_MaxConcurrentConnections_Error,
                        stringResources.ModelName_MaxConcurrentConnections,
                        stringResources.Teradata_DisplayText,
                        'notSupport'
                    ),
                redirectTo: {
                    attribute: Metamodel.modelOf(storeSettings).attribute('maxConcurrentConnections')
                }
            });
        }
        // validate skip line count
        if (
            ((activity.source as DelimitedTextCopySource).formatSettings as DelimitedTextFormatReadSettings).skipLineCount > 0
        ) {
            violations.push({
                message: () =>
                    getConnectionAsSinkDirectCopyErrorMessage(
                        activity,
                        stringResources.CopyCommand_CopyMethod_CopyCommandSkipLineCount_Error,
                        stringResources.SkipLineCountLabel,
                        stringResources.Teradata_DisplayText,
                    ),
                redirectTo: {
                    attribute: DelimitedTextFormatReadSettings.SKIP_LINE_COUNT
                }
            });
        }
    }
    return violations;
}

export function validateDelimitedTextDatasetForTeradata(
    activity: CopyActivity,
    dataset: DelimitedTextDataset,
    directionLabel: string,
    datasetLabel: string
): IViolation[] {
    const violations: IViolation[] = [];
    if (
        !stringIsNullOrEmpty(dataset.compressionCodec) &&
        ![compressionCodecType.gzip, compressionCodecType.none].includes(dataset.compressionCodec)
    ) {
        // compression can be Gzip, or none.
        violations.push({
            message: () =>
                getDirectCopyErrorMessage(
                    directionLabel,
                    format(
                        stringResources.CopyCommand_CopyMethod_CopyCommandSourceCompression_Error,
                        datasetLabel.toLocaleLowerCase(),
                        formatListWithConjunction([compressionCodecType.gzip, compressionCodecType.none])
                    ),
                    stringResources.ModelName_CompressionType,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.COMPRESSION_CODEC
            }
        });
    }
    // encodingName is set to utf-8, which is default value.
    if (!stringIsNullOrEmpty(dataset.encodingName) && dataset.encodingName.toLowerCase() !== Encoding.UTF_8) {
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    format(
                        stringResources.CopyCommand_CopyMethod_CopyCommandEncodingName_Error,
                        datasetLabel,
                        Encoding.UTF_8
                    ),
                    stringResources.ModelName_Encoding,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.ENCODING_NAME
            }
        });
    }
    // check row delimiter format here
    const allowedRowDelimiter =
        dataset.rowDelimiter === rowDelimiters.lineFeed || dataset.rowDelimiter === rowDelimiters.carriageReturnAndLineFeed;
    if (
        !DelimitedTextDataset.ROW_DELIMITER.templateValue(dataset) &&
        !/^(\\u([0-9a-f]){4}|[^])$/.test(dataset.rowDelimiter) &&
        !allowedRowDelimiter
    ) {
        violations.push({
            message: () =>
                getDirectCopyErrorMessage(
                    directionLabel,
                    format(
                        stringResources.Teradata_DirectCopyRowDelimiter_Error,
                        datasetLabel.toLocaleLowerCase()
                    ),
                    stringResources.Dataset_FileFormat_RowDelimiterFieldLabel,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.ROW_DELIMITER
            }
        });
    }
    if (!DelimitedTextDataset.QUOTE_CHAR.templateValue(dataset) && dataset.quoteChar !== '' && dataset.quoteChar !== charValueConst.DoubleQuoteChar) {
        // quote char only supports no quote or double quote
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    format(
                        stringResources.Teradata_DirectCopyQuoteChar_Error,
                        stringResources.Dataset_FileFormat_QuoteCharFieldLabel
                    ),
                    stringResources.Dataset_FileFormat_QuoteCharFieldLabel,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.QUOTE_CHAR
            }
        });
    }
    if (!DelimitedTextDataset.ESCAPE_CHAR.templateValue(dataset) && dataset.escapeChar !== '' && dataset.escapeChar !== charValueConst.DoubleQuoteChar) {
        // escape char only supports no quote or double quote
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    format(
                        stringResources.Teradata_DirectCopyEscapeChar_Error,
                        stringResources.Dataset_FileFormat_EscapeCharFieldLabel
                    ),
                    stringResources.Dataset_FileFormat_EscapeCharFieldLabel,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.ESCAPE_CHAR
            }
        });
    }
    if (!stringIsNullOrEmpty(dataset.nullValue)) {
        // nullValue must set to empty string ("") or null
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    stringResources.CopyCommand_CopyMethod_CopyCommandNullValue_Error,
                    stringResources.NullValueFieldLabel,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.NULL_VALUE
            }
        });
    }
    return violations;
}

/**
 * When support Parquet source settings: Compression type - None, Snappy
 */
export function validateParquetDatasetForTeradata(
    activity: CopyActivity,
    dataset: ParquetDataset,
    datasetLabel: string
): IViolation[] {
    const violations: IViolation[] = [];
    const supportedCodec = [compressionCodecType.none, compressionCodecType.snappy];
    if (
        !stringIsNullOrEmpty(dataset.compressionCodec) &&
        !supportedCodec.map((c) => c.toLowerCase()).includes(dataset.compressionCodec.toLowerCase())
    ) {
        violations.push({
            message: () =>
                getConnectionAsSinkDirectCopyErrorMessage(
                    activity,
                    format(
                        stringResources.CopyCommand_CopyMethod_CopyCommandSourceCompression_Error,
                        datasetLabel,
                        formatListWithConjunction(supportedCodec)
                    ),
                    stringResources.ModelName_CompressionType,
                    stringResources.Teradata_DisplayText
                ),
            redirectTo: {
                entity: dataset,
                attribute: Metamodel.modelOf(dataset).attribute('compressionCodec')
            }
        });
    }
    return violations;
}
